import type { TimeEntryDto, TimeEntryStatus } from '@botguide/shared';
import { formatCents } from '@botguide/shared';

import { Badge, Button, EmptyState, Table } from '@/components';
import type { BadgeTone, Column } from '@/components';
import { EMPTY, TIME_ENTRY_STATUS_LABELS, formatDayShort, formatHours } from '@/lib/format';

/* -------------------------------------------------------------------------- */
/* Earnings, narrowed once                                                    */
/* -------------------------------------------------------------------------- */

/**
 * `earnedCents` is absent, not null, when the viewer is not entitled to it -
 * that is the whole reason it is declared optional in `TimeEntryDto`. Null
 * means the viewer IS entitled but no pay rate was in force on the work date.
 *
 * Four outcomes, one function, so no screen re-derives them and gets the
 * absent/null distinction backwards.
 */
export type EarnedState =
  | { kind: 'not-approved' }
  | { kind: 'not-visible' }
  | { kind: 'no-rate' }
  | { kind: 'earned'; cents: number };

export function earnedState(entry: TimeEntryDto): EarnedState {
  // Pending hours are a claim, not a payment: the API withholds the figure and
  // so does this, even if a stale cache still carries one.
  if (entry.status !== 'approved') return { kind: 'not-approved' };
  if (entry.earnedCents === undefined) return { kind: 'not-visible' };
  if (entry.earnedCents === null) return { kind: 'no-rate' };
  return { kind: 'earned', cents: entry.earnedCents };
}

function earnedLabel(entry: TimeEntryDto): string {
  const state = earnedState(entry);
  switch (state.kind) {
    case 'earned':
      return formatCents(state.cents);
    case 'no-rate':
      return 'No rate';
    case 'not-approved':
    case 'not-visible':
      return EMPTY;
  }
}

/* -------------------------------------------------------------------------- */

const STATUS_TONES: Record<TimeEntryStatus, BadgeTone> = {
  pending: 'warn',
  approved: 'ok',
  rejected: 'danger',
};

export interface TimesheetTableProps {
  entries: readonly TimeEntryDto[];
  /** Cosmetic gates from `useCan`. The API is the boundary, not this. */
  canEdit: boolean;
  canDelete: boolean;
  onEdit: (entry: TimeEntryDto) => void;
  onDelete: (entry: TimeEntryDto) => void;
  /** The entry whose write is in flight, so its controls stop taking clicks. */
  busyId?: string | null;
}

/**
 * One week of logged hours.
 *
 * Only `pending` rows carry an Edit or a Delete. A reviewed entry is frozen
 * server-side and `PATCH` answers 409 - so rendering the control anyway would
 * be offering a button whose only outcome is an error message.
 */
export function TimesheetTable({
  entries,
  canEdit,
  canDelete,
  onEdit,
  onDelete,
  busyId = null,
}: TimesheetTableProps) {
  const showActions = canEdit || canDelete;

  const columns: Column<TimeEntryDto>[] = [
    {
      key: 'date',
      header: 'Date',
      width: '6.5rem',
      cell: (entry) => (
        <span className="font-mono text-[0.8125rem] tnum">{formatDayShort(entry.workDate)}</span>
      ),
    },
    {
      key: 'work',
      header: 'Work',
      cell: (entry) => (
        <div className="min-w-0">
          <p className="truncate font-medium">{entry.taskTitle}</p>
          <p className="truncate text-[0.75rem] text-subtle">{entry.projectName}</p>
        </div>
      ),
    },
    {
      key: 'hours',
      header: 'Hours',
      align: 'right',
      numeric: true,
      width: '6rem',
      cell: (entry) => {
        const billed =
          entry.status === 'approved' &&
          entry.billableMinutes !== null &&
          entry.billableMinutes !== entry.minutes
            ? entry.billableMinutes
            : null;

        return (
          <div>
            <span>{formatHours(entry.minutes)}</span>
            {billed === null ? null : (
              <p className="text-[0.6875rem] text-subtle">billed {formatHours(billed)}</p>
            )}
          </div>
        );
      },
    },
    {
      key: 'status',
      header: 'Status',
      width: '7.5rem',
      cell: (entry) => (
        <Badge tone={STATUS_TONES[entry.status]} size="sm" dot>
          {TIME_ENTRY_STATUS_LABELS[entry.status]}
        </Badge>
      ),
    },
    {
      key: 'earned',
      header: 'Earned',
      align: 'right',
      numeric: true,
      secondary: true,
      width: '6.5rem',
      cell: (entry) => <span className="text-muted">{earnedLabel(entry)}</span>,
    },
    {
      key: 'notes',
      header: 'Notes',
      secondary: true,
      cell: (entry) => (
        <div className="min-w-0 max-w-[22rem]">
          <p className="truncate text-[0.8125rem] text-muted" title={entry.notes || undefined}>
            {entry.notes || EMPTY}
          </p>
          {entry.reviewNote ? (
            <p
              className="truncate text-[0.75rem] text-accent-ink"
              title={entry.reviewNote}
            >
              Review: {entry.reviewNote}
            </p>
          ) : null}
        </div>
      ),
    },
  ];

  if (showActions) {
    columns.push({
      key: 'actions',
      header: <span className="sr-only">Actions</span>,
      align: 'right',
      width: '9rem',
      cell: (entry) =>
        entry.status === 'pending' ? (
          <div className="flex justify-end gap-1.5">
            {canEdit ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busyId === entry.id}
                onClick={() => onEdit(entry)}
              >
                Edit
              </Button>
            ) : null}
            {canDelete ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={busyId === entry.id}
                onClick={() => onDelete(entry)}
                className="text-danger-ink hover:bg-danger-soft"
              >
                Delete
              </Button>
            ) : null}
          </div>
        ) : (
          <span className="text-[0.75rem] text-subtle">Locked</span>
        ),
    });
  }

  return (
    <Table
      columns={columns}
      rows={entries}
      rowKey={(entry) => entry.id}
      caption="Time entries for the selected week"
      rowClassName={(entry) => (entry.status === 'rejected' ? 'opacity-70' : undefined)}
      empty={
        <EmptyState
          title="No hours logged this week"
          icon="hours"
          description="Log time against a task above and it will appear here as pending until it is reviewed."
        />
      }
    />
  );
}
